/**
 * A client's surname typed back to confirm an act that cannot be taken back: recording a death or a discharge
 * (Arthur, A-#171 and A-#173, 27 October 2026). ONE definition, used by the page to enable the button and by the API
 * to refuse before anything is written; record_death()'s own check (064) stays in the database behind both.
 *
 * It is record_death()'s comparison exactly:
 *   lower(trim(typed)) = lower(trim(last_name))
 * PostgreSQL's trim() removes SPACES only, from both ends (never a tab or another blank), and lower() folds case;
 * hyphens, apostrophes and the spaces inside a name are compared as they are. One typed with the client's suffix
 * ("Okafor Jr.") is told so, as record_death() tells it: lower-cased, runs of whitespace, commas and full stops made
 * one space, and trimmed.
 *
 * One known difference, by collation (A-#173 (a)): PostgreSQL's lower() follows the database's collation, and under
 * collation C an accented capital ("É") is not lowered, while toLowerCase() always lowers it. So for an accented
 * surname typed in another case the database may refuse what this accepts. That direction only ever refuses; it never
 * saves a wrong name. test/surname-cases.json records each case for both.
 *
 * This file is copied byte for byte to web/src/surnameConfirm.ts (test/shared-copies.test.ts). It imports nothing.
 */

/** PostgreSQL's trim(): spaces (U+0020) only, from both ends. */
function pgTrim(s: string): string {
  return s.replace(/^ +| +$/g, '');
}

/** Whether the surname typed back is the record's, as record_death() compares them. */
export function surnameMatches(typed: string, recorded: string): boolean {
  return pgTrim(typed).toLowerCase() === pgTrim(recorded).toLowerCase();
}

/** POSIX [[:space:]] in the C locale, with the comma and full stop record_death() folds in. */
const FOLD = /[ \t\n\v\f\r,.]+/g;
const folded = (s: string) => pgTrim(s.toLowerCase().replace(FOLD, ' '));

/** Whether a wrong surname is the surname with the client's suffix on it (record_death()'s second refusal). */
export function suffixTypedWithSurname(typed: string, recorded: string, suffix: string | null): boolean {
  return suffix !== null && folded(typed) === folded(`${recorded} ${suffix}`);
}

/** record_death()'s words for a wrong surname, never echoing it. */
export const SURNAME_REFUSAL = {
  message: 'The surname does not match this client\'s record',
  hint: 'Type the client\'s surname exactly as it appears on the record.',
} as const;
export const SUFFIX_REFUSAL_MESSAGE = 'The suffix is not part of the surname';
export function suffixRefusalHint(suffix: string): string {
  return `This record holds ${suffix} as the client's suffix, separately from the surname. Type the surname without it.`;
}

/**
 * The surname inside the name the page already shows (client.display_name: last_name, then ", " and the first name
 * when there is one). Null when it cannot be told apart: no first name on record, where the surname, a middle name
 * and a suffix run together. The page then waits only for something typed, and the server decides.
 */
export function surnameFromDisplayName(displayName: string): string | null {
  const i = displayName.indexOf(', ');
  return i > 0 ? displayName.slice(0, i) : null;
}
